/**
 * costApi.js - the calls behind Engineers > Staff cost rates (routes/costRates.js)
 * and a project's Tracking tab
 * (routes/projectCosts.js). Each answers { ok, status, body }, never throws.
 * The server decides what a person sees: without 'cost-rates' every cost
 * figure is simply absent from the answers.
 */
import { json, send } from '../../utils/api';

const P = (id) => `/api/project-costs/${encodeURIComponent(id)}`;

// the staff cost table
export const getStaffCosts = () => json('/api/cost-rates');
export const addStaff = (row) => json('/api/cost-rates', send('POST', row));
export const saveStaff = (id, row) => json(`/api/cost-rates/${encodeURIComponent(id)}`, send('PUT', row));
export const deleteStaff = (id) => json(`/api/cost-rates/${encodeURIComponent(id)}`, { method: 'DELETE' });
export const linkStaff = (links) => json('/api/cost-rates/link', send('POST', { links }));
/** Import from Excel: the workbook goes to the server, which finds the Staff table by its headers. */
export async function importStaffFile(file) {
  const fd = new FormData();
  fd.append('file', file);
  return json('/api/cost-rates/import', { method: 'POST', body: fd });
}

// a project
export const getProjectCosts = (id) => json(P(id));
export const saveBudget = (id, data, updatedAt) => json(`${P(id)}/budget`, send('PUT', { data, updatedAt }));
export const previewBudget = (id, data) => json(`${P(id)}/budget/preview`, send('POST', { data }));
export const budgetFromQuote = (id, revision) => json(`${P(id)}/budget/from-quote`, send('POST', { revision }));
export const changeFromQuote = (id, revision) => json(`${P(id)}/budget/change-from-quote`, send('POST', { revision }));
export const applyBudgetToQuoteFields = (id) => json(`${P(id)}/budget/apply`, send('POST', {}));
export const addTimeEntry = (id, row) => json(`${P(id)}/time`, send('POST', row));
export const importTime = (id, rows) => json(`${P(id)}/time/import`, send('POST', { rows }));
export const saveTimeEntry = (id, entryId, row) => json(`${P(id)}/time/${encodeURIComponent(entryId)}`, send('PUT', row));
export const deleteTimeEntry = (id, entryId) => json(`${P(id)}/time/${encodeURIComponent(entryId)}`, { method: 'DELETE' });
export const savePeople = (id, people, remove = []) => json(`${P(id)}/people`, send('PUT', { people, remove }));
export const addMaterial = (id, row) => json(`${P(id)}/materials`, send('POST', row));
export const deleteMaterial = (id, matId) => json(`${P(id)}/materials/${encodeURIComponent(matId)}`, { method: 'DELETE' });
export const savePoLines = (id, lines) => json(`${P(id)}/po-lines`, send('PUT', { lines }));
export const setInvoicePoLine = (id, invoiceId, poLine) =>
  json(`${P(id)}/invoices/${encodeURIComponent(invoiceId)}/po-line`, send('PUT', { poLine }));
export const addExpense = (id, row) => json(`${P(id)}/expenses`, send('POST', row));
export const saveExpense = (id, expId, row) => json(`${P(id)}/expenses/${encodeURIComponent(expId)}`, send('PUT', row));
export const deleteExpense = (id, expId) => json(`${P(id)}/expenses/${encodeURIComponent(expId)}`, { method: 'DELETE' });
export const getCogsRollup = (query = '') => json(`/api/project-costs/rollup${query}`);

/** Upload a receipt into the project's Doc Hub; answers { ok, id } (the attachment id). */
export async function uploadReceipt(projectId, file) {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('doc_type', 'general');
  fd.append('folder', 'costgrids');
  const r = await json(`/api/projects/${encodeURIComponent(projectId)}/attachments`, { method: 'POST', body: fd });
  return { ...r, id: r.body && r.body.id };
}

/** Money and hours as the cost screens show them. */
export { fmtCost as money, fmtHours as hours } from '../../utils/format';
export const pct = (v) => (v === null || v === undefined || !Number.isFinite(Number(v)) ? '-' : `${(Number(v) * 100).toFixed(2)}%`);
